<template>
  <form id="parentInfoForm" @submit.prevent="validateForm">
    <h2 class="text-h2 mt-24">Start your review</h2>
    <TextField
      id="firstName"
      v-model="formDetails.firstName"
      label-name="First name"
      name="first_name"
      type="text"
      class="mt-24"
      autocomplete="given-name"
      :helper-text="formHelperText.firstName"
      :status="formStatuses.firstName"
      @blur="validateField('firstName')"
    />
    <TextField
      id="lastName"
      v-model="formDetails.lastName"
      label-name="Last name"
      name="last_name"
      type="text"
      class="mt-16"
      autocomplete="family-name"
      :helper-text="formHelperText.lastName"
      :status="formStatuses.lastName"
      @blur="validateField('lastName')"
    />
    <TextField
      id="email"
      v-model="formDetails.email"
      label-name="Email"
      name="email"
      type="email"
      class="mt-16"
      autocomplete="email"
      :helper-text="formHelperText.email"
      :status="formStatuses.email"
      @blur="validateField('email')"
    />
    <Checkbox
      v-model="formDetails.acceptTerms"
      :formatted-label-text="termsOfUse"
      class="mt-24"
      label-class=""
      :helper-text="formHelperText.acceptTerms"
      :status="formStatuses.acceptTerms"
    />
    <Button value="Continue" size="medium" type="submit" class="w-full mt-32" />
    <div
      class="flex items-center gap-4 border-grey-divider-subdued border-1 border-solid rounded-lg p-16 bg-grey-background-contrast mt-24"
    >
      <p class="text-body">Already have an account?</p>
      <Button value="Log in" variant="text" href="/login" class="whitespace-nowrap" />
    </div>
  </form>
</template>

<script lang="ts">
import TextField from '@/components/textfield/TextField.vue';
import Checkbox from '@/components/checkbox/Checkbox.vue';
import Button from '@/components/button/Button.vue';
import helperStatus from '@/helpers/status';
import { validateEmail } from '../../../helpers/emailHelper';

export default {
  name: 'ExternalParentInfo',
  components: { TextField, Checkbox, Button },
  props: {
    completeParentInfoForm: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      formDetails: {
        firstName: '',
        lastName: '',
        email: '',
        acceptTerms: false,
      },
      formHelperText: {
        firstName: '',
        lastName: 'Only your first name and last initial will appear on Sittercity',
        email: 'Emails are captured for Trust & Safety, not marketing',
        acceptTerms: '',
      },
      defaultHelperText: {} as Record<string, string>,
      formStatuses: {
        firstName: helperStatus.Neutral,
        lastName: helperStatus.Neutral,
        email: helperStatus.Neutral,
        acceptTerms: helperStatus.Neutral,
      },
    };
  },
  computed: {
    termsOfUse(): string {
      return `I agree to the <a target="_blank" href="/terms" class="link-secondary underline">Terms of Use</a>. For details on collection and use of your information, see our <a target="_blank" href="/privacy" class="link-secondary underline">Privacy Policy</a>.`;
    },
    validationRules(): object {
      return {
        firstName: {
          defaultMessage: this.defaultHelperText.firstName,
          errorMessage: 'First name is required',
          isValid: this.formDetails.firstName.trim() !== '',
        },
        lastName: {
          defaultMessage: this.defaultHelperText.lastName,
          errorMessage: 'Last name is required',
          isValid: this.formDetails.lastName.trim() !== '',
        },
        email: {
          defaultMessage: this.defaultHelperText.email,
          errorMessage: 'Enter an email address in the format name@example.com',
          isValid: this.formDetails.email.trim() !== '' && validateEmail(this.formDetails.email),
        },
        acceptTerms: {
          defaultMessage: this.defaultHelperText.acceptTerms,
          errorMessage: 'You must agree to the Terms of Use',
          isValid: this.formDetails.acceptTerms,
        },
      };
    },
  },
  watch: {
    'formDetails.acceptTerms': {
      handler(val) {
        if (this.formStatuses.acceptTerms === helperStatus.Error && val) {
          this.formStatuses.acceptTerms = helperStatus.Neutral;
          this.formHelperText.acceptTerms = this.defaultHelperText.acceptTerms;
        }
      },
    },
  },
  created() {
    this.defaultHelperText = { ...this.formHelperText };
  },
  methods: {
    validateField(field: string) {
      const rule = this.validationRules[field];
      this.formHelperText[field] = rule.isValid ? rule.defaultMessage : rule.errorMessage;
      this.formStatuses[field] = rule.isValid ? helperStatus.Neutral : helperStatus.Error;
    },
    validateForm() {
      Object.keys(this.validationRules).forEach((field) => {
        const rule = this.validationRules[field];
        this.formHelperText[field] = rule.isValid ? rule.defaultMessage : rule.errorMessage;
        this.formStatuses[field] = rule.isValid ? helperStatus.Neutral : helperStatus.Error;
      });

      const isValidForm = Object.values(this.formStatuses).every((status) => status === helperStatus.Neutral);

      if (isValidForm) {
        this.completeParentInfoForm(this.formDetails.firstName, this.formDetails.lastName, this.formDetails.email);
      }
    },
  },
};
</script>
